// Hook for admin user management in two-tier access system
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApiService } from '@/services/adminApi';
import { createLogger } from '@/utils/logger';
import type { 
  AdminUser, 
  AdminUserFilter, 
  PaginationParams,
  AdminRole 
} from '@/types/admin';
import toast from 'react-hot-toast';

const logger = createLogger('useAdminUsers');

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }

  return fallback;
};

// Query keys for admin user management
export const adminUserKeys = {
  all: ['admin-users'] as const,
  lists: () => [...adminUserKeys.all, 'list'] as const,
  list: (filters: AdminUserFilter, pagination: PaginationParams) => 
    [...adminUserKeys.lists(), filters, pagination] as const,
  pending: () => [...adminUserKeys.all, 'pending'] as const,
  detail: (id: string) => [...adminUserKeys.all, 'detail', id] as const,
};

// Hook to get all admin users
export const useAdminUsers = (
  filters: AdminUserFilter = {},
  pagination: PaginationParams = { page: 1, limit: 25 }
) => {
  return useQuery({
    queryKey: adminUserKeys.list(filters, pagination),
    queryFn: async () => {
      const response = await adminApiService.getAdminUsers(filters, pagination);
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Hook to get pending admin users (awaiting promotion)
export const usePendingAdminUsers = () => {
  return useQuery({
    queryKey: adminUserKeys.pending(),
    queryFn: async () => {
      const response = await adminApiService.getPendingAdminUsers();
      return response;
    },
    staleTime: 1000 * 30, // 30 seconds - more frequent updates for pending users
  });
};

// Hook to promote a user to admin role
export const usePromoteToAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AdminRole }) => {
      const response = await adminApiService.promoteToAdmin(userId, role);
      return response;
    },
    onSuccess: (_data, variables) => {
      toast.success(`User promoted to ${variables.role} successfully`);
      // Invalidate and refetch admin users data
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.pending() });
    },
    onError: (error: unknown) => {
      toast.error(`Failed to promote user: ${getErrorMessage(error, 'Unknown error')}`);
    },
  });
};

export const usePromoteToAdminByEmail = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ email, role, notes, status }: { email: string; role: AdminRole; notes?: string; status?: string }) => {
      const response = await adminApiService.promoteToAdminByEmail(email, role, notes, status);
      return response;
    },
    onSuccess: (_data, _variables) => {
      // Invalidate and refetch admin users data
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.pending() });
    },
    onError: (error: unknown) => {
      logger.error('Role change failed in usePromoteToAdminByEmail:', error);
    },
  });
};

// Hook to revoke admin access
export const useRevokeAdminAccess = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId }: { userId: string }) => {
      const response = await adminApiService.revokeAdminAccess(userId);
      return response;
    },
    onSuccess: (_data, _variables) => {
      toast.success('Admin access revoked successfully');
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.pending() });
    },
    onError: (error: unknown) => {
      toast.error(`Failed to revoke access: ${getErrorMessage(error, 'Unknown error')}`);
    },
  });
};

// Hook to update admin user role
export const useUpdateAdminUserRole = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AdminRole }) => {
      const response = await adminApiService.updateAdminUserRole(userId, role);
      return response;
    },
    onSuccess: (_data, variables) => {
      toast.success(`User role updated to ${variables.role} successfully`);
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.detail(variables.userId) });
    },
    onError: (error: unknown) => {
      toast.error(`Failed to update role: ${getErrorMessage(error, 'Unknown error')}`);
    },
  });
};

// Legacy hooks for compatibility with existing admin endpoints
export const useLegacyAdmins = (
  filters: AdminUserFilter = {},
  pagination: PaginationParams = { page: 1, limit: 25 }
) => {
  return useQuery({
    queryKey: ['legacy-admins', filters, pagination],
    queryFn: async () => {
      const response = await adminApiService.getAdmins(filters, pagination);
      return response;
    },
    staleTime: 1000 * 60 * 5,
  });
};

export const useCreateAdmin = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (adminData: Omit<AdminUser, 'user_id'>) => {
      const response = await adminApiService.createAdmin(adminData);
      return response;
    },
    onSuccess: () => {
      toast.success('Admin user created successfully');
      queryClient.invalidateQueries({ queryKey: ['legacy-admins'] });
      queryClient.invalidateQueries({ queryKey: adminUserKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(`Failed to create admin: ${getErrorMessage(error, 'Unknown error')}`);
    },
  });
};

// Utility function to check if current user has permission for admin management
export const useCanManageAdmins = () => {
  // This will be enhanced when we have proper role checking in useAdminAuth
  // For now, we'll check if user has super_admin role
  return { canManage: true }; // Placeholder - will be implemented with proper auth
};

// Combined hook for admin user management dashboard
export const useAdminUserManagement = (
  filters: AdminUserFilter = {},
  pagination: PaginationParams = { page: 1, limit: 25 }
) => {
  const adminUsers = useAdminUsers(filters, pagination);
  const pendingUsers = usePendingAdminUsers();
  const promoteUser = usePromoteToAdmin();
  const promoteUserByEmail = usePromoteToAdminByEmail();
  const revokeAccess = useRevokeAdminAccess();
  const updateRole = useUpdateAdminUserRole();
  const permissions = useCanManageAdmins();

  return {
    // Data
    adminUsers: adminUsers.data,
    pendingUsers: pendingUsers.data,
    
    // Loading states
    isLoading: adminUsers.isLoading || pendingUsers.isLoading,
    isLoadingAdminUsers: adminUsers.isLoading,
    isLoadingPending: pendingUsers.isLoading,
    
    // Error states
    error: adminUsers.error || pendingUsers.error,
    
    // Actions
    promoteToAdmin: promoteUser.mutate,
    promoteToAdminByEmail: promoteUserByEmail.mutate,
    revokeAccess: revokeAccess.mutate,
    updateRole: updateRole.mutate,
    
    // Action states
    isPromoting: promoteUser.isPending,
    isPromotingByEmail: promoteUserByEmail.isPending,
    isRevoking: revokeAccess.isPending,
    isUpdatingRole: updateRole.isPending,
    
    // Permissions
    canManageAdmins: permissions.canManage,
    
    // Refetch functions
    refetchAdminUsers: adminUsers.refetch,
    refetchPending: pendingUsers.refetch,
  };
};

export default useAdminUserManagement;
